export type TitrationAnalyte = 'strong-acid' | 'weak-acid' | 'strong-base' | 'weak-base';
export type PHTitrationMode = 'ph-scale' | 'titration';
export type EquivalencePrediction = 'acidic' | 'neutral' | 'basic';
export type IndicatorId = 'phenolphthalein' | 'bromothymol-blue' | 'methyl-orange';

export interface TitrationPreset {
  id: string;
  name: string;
  shortDescription: string;
  analyte: TitrationAnalyte;
  analyteLabel: string;
  titrantLabel: string;
  analyteConcentrationM: number;
  titrantConcentrationM: number;
  analyteVolumeMl: number;
  ka?: number;
  kb?: number;
}

export interface TitrationPoint {
  volumeMl: number;
  pH: number;
  stage: 'before equivalence' | 'equivalence' | 'after equivalence';
}

export interface IndicatorDefinition {
  id: IndicatorId;
  name: string;
  range: [number, number];
  midpoint: number;
  lowColour: string;
  middleColour: string;
  highColour: string;
}

export interface ParticleSpecies {
  label: string;
  proportion: number;
  tone: 'coral' | 'sky' | 'mint' | 'sunshine';
}

/** Kw at 25 °C. */
const WATER_ION_PRODUCT = 1e-14;
const EPSILON_MOLES = 1e-10;
/** Ka of ethanoic (acetic) acid at 25 °C. */
export const ACETIC_ACID_KA = 1.8e-5;
/** Kb of ammonia at 25 °C. */
export const AMMONIA_KB = 1.8e-5;
/** Ka of methanoic (formic) acid at 25 °C. */
export const METHANOIC_ACID_KA = 1.8e-4;
/** Highest burette reading the model and UI support, mL. */
export const MAX_BURETTE_VOLUME_ML = 50;
const NEUTRAL_PH = 7;

export interface TitrationDataProvenance {
  sources: string[];
  conditions: string;
  reviewStatus: 'pending-teaching-review';
}

export const TITRATION_DATA_PROVENANCE: TitrationDataProvenance = {
  sources: [
    'Kw = 1.0 × 10⁻¹⁴ at 25 °C (IUPAC / CRC Handbook of Chemistry and Physics)',
    'Ka(CH₃COOH) = 1.8 × 10⁻⁵, Ka(HCOOH) = 1.8 × 10⁻⁴ and Kb(NH₃) = 1.8 × 10⁻⁵ at 25 °C (CRC Handbook of Chemistry and Physics)',
    'Indicator transition ranges from standard analytical chemistry references',
  ],
  conditions:
    '25 °C, dilute aqueous solutions, ideal behaviour (concentrations used for activities), complete mixing.',
  reviewStatus: 'pending-teaching-review',
};

/** Year modes for CT-MODE: Year 10 opens the pH scale; Year 11+ opens titration. */
export const PH_TITRATION_MODES: readonly { mode: PHTitrationMode; fromYear: number }[] = [
  { mode: 'ph-scale', fromYear: 10 },
  { mode: 'titration', fromYear: 11 },
];

export const TITRATION_PRESETS: TitrationPreset[] = [
  {
    id: 'strong-acid-strong-base',
    name: 'Strong acid + strong base',
    shortDescription: '0.100 M HCl titrated with 0.100 M NaOH',
    analyte: 'strong-acid',
    analyteLabel: 'HCl(aq)',
    titrantLabel: 'NaOH(aq)',
    analyteConcentrationM: 0.1,
    titrantConcentrationM: 0.1,
    analyteVolumeMl: 25,
  },
  {
    id: 'weak-acid-strong-base',
    name: 'Weak acid + strong base',
    shortDescription: '0.100 M CH₃COOH titrated with 0.100 M NaOH',
    analyte: 'weak-acid',
    analyteLabel: 'CH₃COOH(aq)',
    titrantLabel: 'NaOH(aq)',
    analyteConcentrationM: 0.1,
    titrantConcentrationM: 0.1,
    analyteVolumeMl: 25,
    ka: ACETIC_ACID_KA,
  },
  {
    id: 'strong-base-strong-acid',
    name: 'Strong base + strong acid',
    shortDescription: '0.100 M NaOH titrated with 0.100 M HCl',
    analyte: 'strong-base',
    analyteLabel: 'NaOH(aq)',
    titrantLabel: 'HCl(aq)',
    analyteConcentrationM: 0.1,
    titrantConcentrationM: 0.1,
    analyteVolumeMl: 25,
  },
  {
    id: 'weak-base-strong-acid',
    name: 'Weak base + strong acid',
    shortDescription: '0.100 M NH₃ titrated with 0.100 M HCl',
    analyte: 'weak-base',
    analyteLabel: 'NH₃(aq)',
    titrantLabel: 'HCl(aq)',
    analyteConcentrationM: 0.1,
    titrantConcentrationM: 0.1,
    analyteVolumeMl: 25,
    kb: AMMONIA_KB,
  },
  {
    id: 'methanoic-acid-strong-base',
    name: 'Methanoic acid + strong base',
    shortDescription: '0.100 M HCOOH titrated with 0.100 M NaOH (Ka ten times larger than ethanoic acid)',
    analyte: 'weak-acid',
    analyteLabel: 'HCOOH(aq)',
    titrantLabel: 'NaOH(aq)',
    analyteConcentrationM: 0.1,
    titrantConcentrationM: 0.1,
    analyteVolumeMl: 25,
    ka: METHANOIC_ACID_KA,
  },
  {
    id: 'dilute-strong-acid-strong-base',
    name: 'Dilute strong acid + strong base',
    shortDescription: '0.0100 M HCl titrated with 0.0100 M NaOH (a smaller pH jump)',
    analyte: 'strong-acid',
    analyteLabel: 'HCl(aq)',
    titrantLabel: 'NaOH(aq)',
    analyteConcentrationM: 0.01,
    titrantConcentrationM: 0.01,
    analyteVolumeMl: 25,
  },
  {
    id: 'concentrated-acid-unequal',
    name: 'Stronger acid sample, unequal concentrations',
    shortDescription: '20.00 mL of 0.150 M HCl titrated with 0.100 M NaOH',
    analyte: 'strong-acid',
    analyteLabel: 'HCl(aq)',
    titrantLabel: 'NaOH(aq)',
    analyteConcentrationM: 0.15,
    titrantConcentrationM: 0.1,
    analyteVolumeMl: 20,
  },
  {
    id: 'ethanoic-acid-unequal',
    name: 'Vinegar-strength sample, unequal concentrations',
    shortDescription: '25.00 mL of 0.0800 M CH₃COOH titrated with 0.100 M NaOH',
    analyte: 'weak-acid',
    analyteLabel: 'CH₃COOH(aq)',
    titrantLabel: 'NaOH(aq)',
    analyteConcentrationM: 0.08,
    titrantConcentrationM: 0.1,
    analyteVolumeMl: 25,
    ka: ACETIC_ACID_KA,
  },
];

export const INDICATORS: IndicatorDefinition[] = [
  {
    id: 'phenolphthalein',
    name: 'Phenolphthalein',
    range: [8.2, 10],
    midpoint: 9.1,
    lowColour: '#f8fafc',
    middleColour: '#f9a8d4',
    highColour: '#ec4899',
  },
  {
    id: 'bromothymol-blue',
    name: 'Bromothymol blue',
    range: [6, 7.6],
    midpoint: 6.8,
    lowColour: '#fde047',
    middleColour: '#86efac',
    highColour: '#60a5fa',
  },
  {
    id: 'methyl-orange',
    name: 'Methyl orange',
    range: [3.1, 4.4],
    midpoint: 3.75,
    lowColour: '#ef4444',
    middleColour: '#fb923c',
    highColour: '#fde047',
  },
];

function clampPH(value: number) {
  return Math.min(14, Math.max(0, value));
}

function negativeLog10(value: number) {
  return -Math.log10(Math.max(value, 1e-14));
}

export function equivalenceVolumeMl(preset: TitrationPreset) {
  return (preset.analyteConcentrationM * preset.analyteVolumeMl) / preset.titrantConcentrationM;
}

export function calculateTitrationPoint(
  preset: TitrationPreset,
  deliveredVolumeMl: number,
): TitrationPoint {
  const volumeMl = Math.max(0, deliveredVolumeMl);
  const analyteMoles = (preset.analyteConcentrationM * preset.analyteVolumeMl) / 1000;
  const titrantMoles = (preset.titrantConcentrationM * volumeMl) / 1000;
  const totalVolumeL = (preset.analyteVolumeMl + volumeMl) / 1000;
  const difference = analyteMoles - titrantMoles;
  const atEquivalence = Math.abs(difference) <= EPSILON_MOLES;
  const stage = atEquivalence
    ? 'equivalence'
    : difference > 0
      ? 'before equivalence'
      : 'after equivalence';

  if (preset.analyte === 'strong-acid') {
    if (atEquivalence) return { volumeMl, pH: NEUTRAL_PH, stage };
    const concentration = Math.abs(difference) / totalVolumeL;
    const pH = difference > 0 ? negativeLog10(concentration) : 14 + Math.log10(concentration);
    return { volumeMl, pH: clampPH(pH), stage };
  }

  if (preset.analyte === 'strong-base') {
    if (atEquivalence) return { volumeMl, pH: NEUTRAL_PH, stage };
    const concentration = Math.abs(difference) / totalVolumeL;
    const pH = difference > 0 ? 14 + Math.log10(concentration) : negativeLog10(concentration);
    return { volumeMl, pH: clampPH(pH), stage };
  }

  // Weak acid (titrated with strong base) or weak base (titrated with strong acid).
  const isWeakAcid = preset.analyte === 'weak-acid';
  const k = isWeakAcid ? (preset.ka ?? ACETIC_ACID_KA) : (preset.kb ?? AMMONIA_KB);
  /** Converts [H₃O⁺] (weak acid) or [OH⁻] (weak base) into pH. */
  const toPH = (concentration: number) =>
    isWeakAcid ? negativeLog10(concentration) : 14 + Math.log10(concentration);

  // At equivalence only the conjugate remains; it reacts with water (K = Kw / k).
  const conjugateConcentration = analyteMoles / totalVolumeL;
  const hydrolysed = Math.sqrt((WATER_ION_PRODUCT / k) * conjugateConcentration);
  // A weak acid's conjugate base makes OH⁻; a weak base's conjugate acid makes H₃O⁺.
  const equivalencePH = isWeakAcid ? 14 + Math.log10(hydrolysed) : negativeLog10(hydrolysed);

  if (atEquivalence) return { volumeMl, pH: clampPH(equivalencePH), stage };

  if (difference > EPSILON_MOLES) {
    // Equilibrium with the conjugate already present: k = x(c_conj + x)/(c_weak − x).
    const weakConcentration = difference / totalVolumeL;
    const conjugateNow = titrantMoles / totalVolumeL;
    const b = conjugateNow + k;
    const x = (-b + Math.sqrt(b * b + 4 * k * weakConcentration)) / 2;
    const pH = toPH(x);
    // Water and hydrolysis are ignored before equivalence, so never cross the equivalence pH.
    const bounded = isWeakAcid ? Math.min(pH, equivalencePH) : Math.max(pH, equivalencePH);
    return { volumeMl, pH: clampPH(bounded), stage };
  }

  const excessTitrant = Math.abs(difference) / totalVolumeL;
  const pH = isWeakAcid ? 14 + Math.log10(excessTitrant) : negativeLog10(excessTitrant);
  return { volumeMl, pH: clampPH(pH), stage };
}

export function titrationCurve(preset: TitrationPreset, maximumVolumeMl = 50) {
  return Array.from({ length: maximumVolumeMl + 1 }, (_, volumeMl) =>
    calculateTitrationPoint(preset, volumeMl),
  );
}

export function equivalencePrediction(preset: TitrationPreset): EquivalencePrediction {
  const pH = calculateTitrationPoint(preset, equivalenceVolumeMl(preset)).pH;
  if (pH < 6.5) return 'acidic';
  if (pH > 7.5) return 'basic';
  return 'neutral';
}

export function indicatorById(id: IndicatorId) {
  return INDICATORS.find((indicator) => indicator.id === id) ?? INDICATORS[0];
}

export function indicatorColour(indicator: IndicatorDefinition, pH: number) {
  if (pH < indicator.range[0]) return indicator.lowColour;
  if (pH > indicator.range[1]) return indicator.highColour;
  return indicator.middleColour;
}

export function indicatorEndpointVolumeMl(preset: TitrationPreset, indicator: IndicatorDefinition) {
  let low = 0;
  let high = 50;
  const curveIncreases = preset.analyte === 'strong-acid' || preset.analyte === 'weak-acid';
  for (let iteration = 0; iteration < 60; iteration += 1) {
    const midpoint = (low + high) / 2;
    const midpointPH = calculateTitrationPoint(preset, midpoint).pH;
    if (curveIncreases ? midpointPH < indicator.midpoint : midpointPH > indicator.midpoint) {
      low = midpoint;
    } else {
      high = midpoint;
    }
  }
  return (low + high) / 2;
}

export function particleSpecies(
  preset: TitrationPreset,
  deliveredVolumeMl: number,
): ParticleSpecies[] {
  const equivalence = equivalenceVolumeMl(preset);
  const progress = Math.min(1, deliveredVolumeMl / equivalence);
  const after = Math.max(0, (deliveredVolumeMl - equivalence) / equivalence);

  if (preset.analyte === 'weak-acid') {
    return [
      { label: 'HA', proportion: Math.max(0, 1 - progress), tone: 'coral' },
      { label: 'A⁻', proportion: Math.max(0.12, Math.min(1, progress)), tone: 'sky' },
      { label: 'H₃O⁺', proportion: deliveredVolumeMl === 0 ? 0.12 : 0, tone: 'sunshine' },
      { label: 'OH⁻', proportion: Math.min(1, after), tone: 'mint' },
    ];
  }
  if (preset.analyte === 'weak-base') {
    return [
      { label: 'NH₃', proportion: Math.max(0, 1 - progress), tone: 'sky' },
      { label: 'NH₄⁺', proportion: Math.max(0.12, Math.min(1, progress)), tone: 'coral' },
      { label: 'OH⁻', proportion: deliveredVolumeMl === 0 ? 0.12 : 0, tone: 'mint' },
      { label: 'H₃O⁺', proportion: Math.min(1, after), tone: 'sunshine' },
    ];
  }
  if (preset.analyte === 'strong-acid') {
    return [
      { label: 'H₃O⁺', proportion: Math.max(0, 1 - progress), tone: 'coral' },
      { label: 'Na⁺ / Cl⁻', proportion: 0.65, tone: 'sky' },
      { label: 'OH⁻', proportion: Math.min(1, after), tone: 'mint' },
    ];
  }
  return [
    { label: 'OH⁻', proportion: Math.max(0, 1 - progress), tone: 'mint' },
    { label: 'Na⁺ / Cl⁻', proportion: 0.65, tone: 'sky' },
    { label: 'H₃O⁺', proportion: Math.min(1, after), tone: 'coral' },
  ];
}

export function checkEquivalenceAnswer(
  preset: TitrationPreset,
  rawValue: string,
  unit: 'mL' | 'L',
) {
  const numeric = Number(rawValue);
  if (!Number.isFinite(numeric) || numeric < 0) return false;
  const answerMl = unit === 'L' ? numeric * 1000 : numeric;
  return Math.abs(answerMl - equivalenceVolumeMl(preset)) <= 0.1;
}

// ---------------------------------------------------------------------------
// Buffer model: ethanoic acid / ethanoate, Henderson–Hasselbalch at 25 °C
// ---------------------------------------------------------------------------

export interface BufferSystem {
  id: string;
  name: string;
  weakAcidLabel: string;
  conjugateBaseLabel: string;
  ka: number;
  acidConcentrationM: number;
  baseConcentrationM: number;
  volumeMl: number;
}

export const ACETATE_BUFFER: BufferSystem = {
  id: 'acetate-buffer',
  name: 'Ethanoic acid / sodium ethanoate buffer',
  weakAcidLabel: 'CH₃COOH',
  conjugateBaseLabel: 'CH₃COO⁻',
  ka: ACETIC_ACID_KA,
  acidConcentrationM: 0.1,
  baseConcentrationM: 0.1,
  volumeMl: 100,
};

/** Henderson–Hasselbalch is a good model while the base:acid ratio stays within 0.1–10. */
const EFFECTIVE_RATIO_MIN = 0.1;
const EFFECTIVE_RATIO_MAX = 10;

export interface BufferResult {
  /** Added strong base (+) or strong acid (−), in mmol. */
  addedMmol: number;
  bufferedPH: number;
  unbufferedPH: number;
  acidMmol: number;
  baseMmol: number;
  withinCapacity: boolean;
  inEffectiveRange: boolean;
  /** Largest amount of strong acid / base the buffer can neutralise, in mmol. */
  acidCapacityMmol: number;
  baseCapacityMmol: number;
}

/** pH of pure water after adding strong acid (−) or base (+), volume change ignored. */
export function unbufferedPH(addedMmol: number, volumeMl: number) {
  if (addedMmol === 0) return NEUTRAL_PH;
  const concentration = Math.abs(addedMmol) / volumeMl;
  return clampPH(addedMmol > 0 ? 14 + Math.log10(concentration) : negativeLog10(concentration));
}

export function bufferPH(system: BufferSystem, addedMmol: number): BufferResult {
  if (!Number.isFinite(addedMmol)) throw new Error('Added amount must be a finite number');
  const initialAcid = system.acidConcentrationM * system.volumeMl;
  const initialBase = system.baseConcentrationM * system.volumeMl;
  const acidMmol = initialAcid - addedMmol;
  const baseMmol = initialBase + addedMmol;
  const pKa = negativeLog10(system.ka);
  let pH: number;
  let withinCapacity = true;
  if (acidMmol > 0 && baseMmol > 0) {
    pH = pKa + Math.log10(baseMmol / acidMmol);
  } else if (acidMmol <= 0) {
    // All the weak acid is used up: excess hydroxide, or only the conjugate base left.
    withinCapacity = false;
    const excess = -acidMmol;
    pH =
      excess > 0
        ? 14 + Math.log10(excess / system.volumeMl)
        : 14 + Math.log10(Math.sqrt((WATER_ION_PRODUCT / system.ka) * (baseMmol / system.volumeMl)));
  } else {
    withinCapacity = false;
    const excess = -baseMmol;
    const acidConcentration = acidMmol / system.volumeMl;
    pH =
      excess > 0
        ? negativeLog10(excess / system.volumeMl)
        : negativeLog10((-system.ka + Math.sqrt(system.ka ** 2 + 4 * system.ka * acidConcentration)) / 2);
  }
  const ratio = withinCapacity ? baseMmol / acidMmol : Number.NaN;
  return {
    addedMmol,
    bufferedPH: clampPH(pH),
    unbufferedPH: unbufferedPH(addedMmol, system.volumeMl),
    acidMmol: Math.max(0, acidMmol),
    baseMmol: Math.max(0, baseMmol),
    withinCapacity,
    inEffectiveRange: withinCapacity && ratio >= EFFECTIVE_RATIO_MIN && ratio <= EFFECTIVE_RATIO_MAX,
    acidCapacityMmol: initialBase,
    baseCapacityMmol: initialAcid,
  };
}
